<template>
  <section class="w-full h-full">
    <div id="designer" class="w-full" style="height: calc(100vh - 70px)"></div>

    <BaseModalEditCreate
      v-if="schemaModalActive"
      :mode="mode"
      title="SQL Script"
      :is-modified="false"
      max-width="extra"
      @save="loadSchema"
      @dismiss-changes="schemaModalActive = false">
      <template #form>
        <div class="grid grid-rows-[1fr_auto] gap-4">
          <textarea
            v-model.trim="reportingTemplate.datasourceScript"
            rows="8"
            class="w-full font-mono border rounded-lg p-2"
            placeholder="SELECT * FROM my_table LIMIT 0" />
          <div v-if="paramWarning" class="text-xs text-red-500">
            <span>{{ paramWarning }}</span>
          </div>

          <div v-if="paramMetadata.name" class="border rounded-lg p-4 bg-base-50">
            <h4 class="text-sm text-gray-500 mb-3">Parameter Configuration</h4>
            <div class="grid grid-cols-3 gap-5 items-start">
              <div>
                <label class="block text-xs text-gray-400 mb-1">Name</label>
                <p class="text-gray-700 py-2">{{ paramMetadata.name }}</p>
              </div>
              <div>
                <label class="block text-xs text-gray-400 mb-1">Type</label>
                <BaseSelectDropdown
                  :items="dropDownParamTypes"
                  :selected-item="selectedParamType"
                  name="documentType"
                  mode="edit"
                  clearable
                  :error="$v.paramType.required.$invalid ? $v.paramType.required.$message : ''"
                  @update:selected="handleParamTypeSelection" />
              </div>
              <div>
                <label class="block text-xs text-gray-400 mb-1">Default Value</label>
                <BaseInput
                  color="base"
                  :type="paramMetadata.type === 'number' ? 'number' : 'text'"
                  :error="$v.paramValue.required.$invalid ? $v.paramValue.required.$message : ''"
                  v-model="paramMetadata.defaultValue" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </BaseModalEditCreate>

    <BaseModalEditCreate
      v-if="onSaveModalActive"
      :mode="mode"
      :is-modified="false"
      @dismiss-changes="onSaveModalActive = false"
      @save="onSaveReport">
      <template #form>
        <div>
          <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
          <BaseInput v-model="reportingTemplate.name" color="base" />
        </div>
        <div>
          <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
          <BaseInput v-model="reportingTemplate.code" color="base" />
        </div>
        <BaseToggle label="Client" name="client" v-model="reportingTemplate.isClient" />
        <BaseToggle label="Manager" name="manager" v-model="reportingTemplate.isManager" />
        <BaseToggle
          :label="$t('baseObject.isActive')"
          name="active"
          v-model="reportingTemplate.isActive" />
      </template>
    </BaseModalEditCreate>
  </section>
</template>

<script setup lang="ts">
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import {
  addOrEditReportingTemplate,
  getReportingSchema,
  getReportingTemplateById,
} from '@/services/api/reporting';
import { useLookupsStore } from '@/stores/lookups';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import type { SelectDropdownItem } from '@/types/Base';
import type { ParamMeta, ReportingTemplate, ParamType } from '@/types/Reporting';
import type { Stimulsoft as StimulsoftNS } from 'stimulsoft-reports-js';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

type StiDesigner = StimulsoftNS.Designer.StiDesigner;
interface StiSaveReportEventArgs {
  fileName: string | null;
  report: StimulsoftNS.Report.StiReport;
  async: boolean;
  preventDefault: boolean;
  callback: () => void;
}

// Access Stimulsoft safely at runtime
function getStimulsoft() {
  if (typeof window === 'undefined' || !(window as any).Stimulsoft) {
    throw new Error('Stimulsoft scripts not loaded on window');
  }
  return (window as any).Stimulsoft as typeof StimulsoftNS;
}

const { t: $t } = useI18n({ useScope: 'global' });
const route = useRoute();
const toast = useToastStore();
const { reloadSingleLookup } = useLookupsStore();

const designer = ref<StiDesigner>();
const report = ref<StimulsoftNS.Report.StiReport>();
const MAX_TOOLBAR_INJECTION_ATTEMPTS = 10;
const TOOLBAR_INJECTION_DELAY_MS = 150;

const mode = ref<'add' | 'edit'>('add');
const schemaModalActive = ref(false);
const onSaveModalActive = ref(false);
const saveDialogArgs = ref<StiSaveReportEventArgs>();
const reportingTemplate = ref<ReportingTemplate>(initReportingTemplate());
const selectedParamType = ref<SelectDropdownItem | null>(null);
const dropDownParamTypes = ref<SelectDropdownItem[]>([
  { name: 'String', value: 'string' },
  { name: 'GUID', value: 'guid' },
  { name: 'Number', value: 'number' },
]);
const paramMetadata = ref<ParamMeta>({ name: null, type: null, defaultValue: '' });

onBeforeUnmount(() => {
  destroyDesigner();
});

onMounted(async () => {
  const res = await getReportingTemplateById(route.params.id as string);
  if (res) {
    mode.value = 'edit';
    reportingTemplate.value = res;
    if (res?.parametersJson) {
      const meta = JSON.parse(res.parametersJson) as Record<string, any>;
      const paramName = Object.keys(meta)[0];
      if (paramName) {
        paramMetadata.value = {
          name: paramName,
          type: meta[paramName].type as ParamType,
          defaultValue: meta[paramName].default ?? '',
        };

        selectedParamType.value =
          dropDownParamTypes.value.find((p) => p.value === meta[paramName].type) ?? null;
      }
    }
  }
  createDesigner(res ?? null);
});

function initReportingTemplate() {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    isActive: true,
    isFromManager: true,
    template: '',
    functionality: undefined,
    datasourceScript: '',
    documentTypeId: '',
    isManager: false,
    isClient: false,
  } as ReportingTemplate;
}

async function createDesigner(existing?: ReportingTemplate | null) {
  destroyDesigner();
  const Stimulsoft = getStimulsoft();

  // Create options
  const options = new Stimulsoft.Designer.StiDesignerOptions();
  options.width = '100%';
  options.height = '100%';
  options.appearance.fullScreenMode = false;
  options.toolbar.showPublishButton = false;
  (options as any).showSaveDialog = false;

  // Create designer
  designer.value = markRaw(new Stimulsoft.Designer.StiDesigner(options, 'StiDesigner', false));

  //Create report
  const newReport = markRaw(new Stimulsoft.Report.StiReport());
  if (existing?.template) {
    const tpl =
      typeof existing.template === 'string' ? existing.template : JSON.stringify(existing.template);
    newReport.load(tpl);
  }

  designer.value.report = newReport;
  designer.value.renderHtml('designer');

  designer.value.onSaveReport = intercept;
  designer.value.onSaveAsReport = intercept;

  injectRibbonButtonLoadSchema(designer.value);

  if (existing?.datasourceScript) {
    try {
      const bag: Record<string, unknown> = {};
      if (reportingTemplate.value?.parametersJson) {
        const meta = JSON.parse(reportingTemplate.value.parametersJson);
        for (const [k, v] of Object.entries(meta)) {
          bag[k] = (v as any).default ?? null;
        }
      }

      const schema = await getReportingSchema(reportingTemplate.value.datasourceScript!, bag);
      const ds = new Stimulsoft.System.Data.DataSet('Schema');
      ds.readJson(schema);
      newReport.regData('Schema', 'Schema', ds);
      newReport.dictionary?.synchronize();

      designer.value.report = newReport;
    } catch (error) {
      console.error('Schema load failed', error);
    }
  }

  report.value = newReport;
}

function injectRibbonButtonLoadSchema(designer: StimulsoftNS.Designer.StiDesigner, attempt = 0) {
  if (!designer) return;
  const toolBarRow = (designer as any)?.jsObject?.options?.toolBar?.firstChild?.tr?.[0];
  if (!toolBarRow) {
    if (attempt < MAX_TOOLBAR_INJECTION_ATTEMPTS) {
      window.setTimeout(
        () => injectRibbonButtonLoadSchema(designer, attempt + 1),
        TOOLBAR_INJECTION_DELAY_MS
      );
    }
    return;
  }
  if (document.getElementById('btnLoadSchema')) return;

  const jsObject = (designer as any).jsObject;
  if (typeof jsObject?.ToolButton !== 'function') return;
  let loadSchemaBtn: any;
  try {
    loadSchemaBtn = jsObject.ToolButton('btnLoadSchema', 'Load schema');
  } catch (error) {
    console.error('Failed to create Stimulsoft toolbar button', error);
    return;
  }

  if (!loadSchemaBtn) return;
  const cell = document.createElement('td');
  cell.className = 'stiDesignerToolButtonCell';
  cell.appendChild(loadSchemaBtn);
  toolBarRow.insertBefore(cell, toolBarRow.childNodes[6]);

  loadSchemaBtn.id = 'btnLoadSchema';
  loadSchemaBtn.action = () => (schemaModalActive.value = true);
}

function destroyDesigner() {
  if (designer.value) {
    const maybeDisposable = designer.value as unknown as { dispose?: () => void };
    if (typeof maybeDisposable.dispose === 'function') {
      try {
        maybeDisposable.dispose();
      } catch (error) {
        console.error('Failed to dispose Stimulsoft designer', error);
      }
    }
    designer.value = undefined;
  }

  report.value = undefined;

  const container = document.getElementById('designer');
  if (container) container.innerHTML = '';
}

function handleParamTypeSelection(item: SelectDropdownItem) {
  selectedParamType.value = item ? item : null;
  if (item && paramMetadata.value.name) {
    paramMetadata.value.type = item.value as ParamType;
    paramMetadata.value.defaultValue = item.value === 'number' ? 0 : '';
  }
}

function validateSql(sql: string): boolean {
  const forbidden =
    /(\b(insert|update|delete|drop|alter|truncate|merge|into|create|grant|revoke)\b|--|;)/i;
  return !forbidden.test(sql);
}

const validationModel = ref({
  paramType: paramMetadata.value.type || null,
  paramValue: paramMetadata.value.defaultValue || '',
});

const rules = computed(() => ({
  paramType: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  paramValue: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

async function loadSchema() {
  $v.value.$touch();
  if ($v.value.$invalid || paramWarning.value) return;

  const Stimulsoft = getStimulsoft();
  const sql = (reportingTemplate.value.datasourceScript ?? '').trim();
  if (!sql || !validateSql(sql)) {
    toast.error({ text: $t('reporting.unsafeSql'), timeout: 5000 });
    return;
  }

  const bag: Record<string, unknown> = {};
  if (paramMetadata.value.name) {
    bag[paramMetadata.value.name] = paramMetadata.value.defaultValue ?? null;
  }

  try {
    const schema = await getReportingSchema(sql, bag);
    const dataSet = new Stimulsoft.System.Data.DataSet('Schema');
    dataSet.readJson(schema);
    if (report.value) {
      report.value.dictionary.databases.clear();
      report.value.dictionary.dataSources.clear();

      report.value.regData('Schema', 'Schema', dataSet);
      report.value.dictionary.synchronize();

      if (designer.value) {
        // reassign to make sure designer reflects the update
        designer.value.report = report.value;
        designer.value.renderHtml('designer');
        injectRibbonButtonLoadSchema(designer.value);
      }
    } else {
      const newReport = markRaw(new Stimulsoft.Report.StiReport());
      newReport.regData('Schema', 'Schema', dataSet);
      newReport.dictionary?.synchronize();

      if (designer.value) designer.value.report = newReport;
      report.value = newReport;
    }

    schemaModalActive.value = false;
    toast.success({ text: 'Schema loaded', timeout: 3000 });
  } catch (e) {
    console.error('Schema load failed', e);
    toast.error({ text: 'Failed to load schema', timeout: 4000 });
  }
}

const DOLLAR_PARAM_REGEX = /\$\$([A-Za-z_][A-Za-z0-9_]*)/g;

const paramWarning = ref<string | null>(null);

function extractDollarParameter(sql: string): { paramName: string | null; warning: string | null } {
  const matches: string[] = [];
  sql.replace(DOLLAR_PARAM_REGEX, (_, p) => {
    matches.push(p);
    return '';
  });

  if (matches.length === 0) return { paramName: null, warning: null };
  if (matches.length === 1) return { paramName: matches[0], warning: null };

  const uniqueMatches = [...new Set(matches)];
  if (uniqueMatches.length === 1) return { paramName: uniqueMatches[0], warning: null };

  return { paramName: null, warning: $t('reporting.multipleParams') };
}

watch(
  () => reportingTemplate.value.datasourceScript,
  (sql) => {
    const { paramName, warning } = extractDollarParameter(sql ?? '');
    paramWarning.value = warning;
    if (paramName && paramName !== paramMetadata.value.name) {
      paramMetadata.value = { name: paramName, type: null, defaultValue: '' };
      selectedParamType.value = null;
    }
  },
  { immediate: true }
);

const intercept = (args: any) => {
  const Stimulsoft = getStimulsoft();
  args.preventDefault = true;
  onSaveModalActive.value = true;
  saveDialogArgs.value = args;
  Stimulsoft.System.StiError.showError('Template saved!', false);
};

async function onSaveReport() {
  reportingTemplate.value.template = saveDialogArgs.value?.report.saveToJsonString();
  reportingTemplate.value.parametersJson = paramMetadata.value.name
    ? JSON.stringify({
        [paramMetadata.value.name]: {
          type: paramMetadata.value.type,
          default: paramMetadata.value.defaultValue,
        },
      })
    : null;

  const res = await addOrEditReportingTemplate(reportingTemplate.value);
  if (res) {
    await reloadSingleLookup('reportingTemplates', true);
    toast.success({ text: 'Saved', timeout: 3000 });
  }
  onSaveModalActive.value = false;
}

watch(
  () => paramMetadata.value,
  (newData) => {
    if (!newData) return;

    validationModel.value.paramType = newData.type || null;
    validationModel.value.paramValue = newData.defaultValue || '';
  },
  { immediate: true, deep: true }
);
</script>

<style lang="css">
.stiDesignerTreeItemIconOpening_Mouse {
  margin: 0 12px 0 0 !important;
}
</style>
